import { useEffect, useState } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { ROLES, ROLE_LABEL } from "../../lib/roleOptions";
import TerritoryPicker from "../../components/common/TerritoryPicker";
import { FIELD_ROLES } from "../../lib/population";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import { Loader2 } from "lucide-react";
import MasterChangeDestination from "../../components/common/MasterChangeDestination";

const UNASSIGNED = "__unassigned__";

export default function UserEditDialog({ open, user, managers, onClose, onSave }) {
  const [role, setRole] = useState("");
  const [managerId, setManagerId] = useState(UNASSIGNED);
  const [territories, setTerritories] = useState([]);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && user) {
      setRole(user.role);
      setManagerId(user.manager_id || UNASSIGNED);
      setTerritories(user.territory_unit_ids || []);
      setFullName(user.full_name || "");
      setPhone(user.phone || "");
      setError("");
    }
  }, [open, user]);

  if (!user) return null;

  const eligibleManagers = managers.filter((m) => m.id !== user.id);

  function buildPatch() {
    const patch = {};
    if (role && role !== user.role) patch.role = role;
    const newManager = managerId === UNASSIGNED ? null : managerId;
    if (newManager !== (user.manager_id || null)) patch.manager_id = newManager;
    // Order-insensitive compare: reordering units is not a change.
    const before = [...(user.territory_unit_ids || [])].sort().join("|");
    if ([...territories].sort().join("|") !== before) patch.territory_unit_ids = territories;
    if (fullName !== (user.full_name || "")) patch.full_name = fullName.trim();
    if (phone !== (user.phone || "")) patch.phone = phone || null;
    return patch;
  }

  async function handleSubmit() {
    setError("");
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      setError("Nothing to update.");
      return;
    }
    if (patch.full_name === "") {
      setError("Full name cannot be empty.");
      return;
    }
    setSubmitting(true);
    try {
      await onSave(user.id, patch);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
          <DialogDescription>
            Update role, manager, territory, and contact details for{" "}
            <span className="font-semibold text-callisto-navy">{user.full_name}</span>.
            {/* SAVE FILES, IT DOES NOT APPLY (A26/A28, D-006). Named here, in
                the future tense, with the approver resolved from the chain —
                the same sentence the Archive and Deactivate dialogs carry. */}
            <span className="block mt-2">
              <MasterChangeDestination testId="user-edit-destination" />
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-callisto-navy">Full name</Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
              data-testid="user-edit-name"
            />
          </div>
          <div>
            <Label className="text-callisto-navy">Email</Label>
            <Input value={user.email} disabled className="h-11 mt-1.5 bg-slate-50 text-callisto-muted" />
            <p className="mt-1 text-[11px] text-callisto-muted">Email cannot be changed. Re-invite the user with a different email if needed.</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-callisto-navy">Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="user-edit-role">
                  <SelectValue placeholder="Pick role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-callisto-navy">Phone</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91-…"
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="user-edit-phone"
              />
            </div>
          </div>
          <div>
            <Label className="text-callisto-navy">Manager</Label>
            <Select value={managerId} onValueChange={setManagerId}>
              <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="user-edit-manager">
                <SelectValue placeholder="Pick manager" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                {eligibleManagers.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.full_name} ({ROLE_LABEL[m.role] || m.role})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-callisto-navy">Territory units</Label>
            {/* Multi-select from the territory master — free text is gone.
                A user flagged by the migration keeps their old string visible
                here until this edit resolves it. */}
            {/* The needs-review sentence is <TerritoryLabel/>'s, not this
                dialog's (RC-10). It said the same thing in different words —
                and branched on the literal value "Patna" to choose between
                "s this covers" and "(s) this maps to", which is a rule about
                one row's data written into the copy. The remediation line
                below is this surface's own, because only the edit side can
                offer it. */}
            {user.territory_unmigrated && (
              <div className="mt-1 text-[11px]" data-testid="user-edit-unmigrated-note">
                <TerritoryLabel value={user.territory} unmigrated />
                <p className="text-callisto-muted mt-0.5">Pick the unit or units it maps to.</p>
              </div>
            )}
            <TerritoryPicker
              fieldRole={FIELD_ROLES.includes(role || user.role)}
              value={territories}
              onChange={setTerritories}
              testIdPrefix="user-edit-territories"
            />
          </div>
          {error && (
            <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2" data-testid="user-edit-error">
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
            data-testid="user-edit-save"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
